/**
 * PropertyChangesEditor Component
 * 
 * UI to define how properties change during transitions.
 * Supports: Set Value, Increment, Decrement, Copy From, Calculate
 */

import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Card, Button, Select, Input, InputNumber, Space, Tooltip, Tag, Collapse } from 'antd';
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { PropertySelector } from './PropertySelector';
import { ArtifactItemFilterEditor } from './ArtifactItemFilterEditor';
import { PropertyChange, ArtifactItemFilter } from '../../types/transitionParameters';
import { apiClient } from '../../services/apiClient';
import styles from './PropertyChangesEditor.module.css';

const { Panel } = Collapse;

const { Option } = Select;

interface PropertyDefinition {
  property_key?: string;
  name?: string;
  property_type?: string;
  type?: string;
  description?: string;
}

interface PropertyChangesEditorProps {
  propertyChanges: PropertyChange[];
  onChange: (changes: PropertyChange[]) => void;
  artifactProperties: PropertyDefinition[];
  disabled?: boolean;
}

export const PropertyChangesEditor: React.FC<PropertyChangesEditorProps> = ({
  propertyChanges = [],
  onChange,
  artifactProperties = [],
  disabled = false,
}) => {
  // Ensure artifactProperties is always an array
  const safeProperties = Array.isArray(artifactProperties) ? artifactProperties : [];
  const [selectedIndex, setSelectedIndex] = useState<number>(propertyChanges.length > 0 ? 0 : -1);

  const selectedChange = useMemo(() => {
    if (selectedIndex < 0 || selectedIndex >= propertyChanges.length) return null;
    return propertyChanges[selectedIndex];
  }, [propertyChanges, selectedIndex]);

  const handleAddChange = () => {
    const newChange: PropertyChange = {
      property_key: '',
      change_type: 'set',
      target_value: null,
    };
    const updated = [...propertyChanges, newChange];
    onChange(updated);
    setSelectedIndex(updated.length - 1);
  };

  const handleRemoveChange = (index: number) => {
    const updated = propertyChanges.filter((_, i) => i !== index);
    onChange(updated);
    if (updated.length === 0) {
      setSelectedIndex(-1);
    } else if (index === selectedIndex) {
      setSelectedIndex(Math.max(0, index - 1));
    } else if (index < selectedIndex) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  // Helper to generate date range expression
  const generateDateRangeExpression = (updated: PropertyChange[], index: number, startProp: string, endProp: string | null) => {
    const change = updated[index] as any;
    
    // Get the values for true/false cases
    const valueIfTrue = change.date_range_value_if_true ?? true;
    const valueIfFalse = change.date_range_value_if_false ?? false;
    
    // Generate expression: if endProp is null, just check if now >= startProp
    // Otherwise: now >= startProp AND (endProp IS NULL OR now <= endProp)
    let expression: string;
    
    if (!endProp) {
      // Simple case: just check if now >= startProp
      expression = `{{now}} >= {{${startProp}}} ? ${JSON.stringify(valueIfTrue)} : ${JSON.stringify(valueIfFalse)}`;
    } else {
      // Complex case: check if now is between startProp and endProp (or endProp is null)
      expression = `({{now}} >= {{${startProp}}} AND ({{${endProp}}} IS NULL OR {{now}} <= {{${endProp}}})) ? ${JSON.stringify(valueIfTrue)} : ${JSON.stringify(valueIfFalse)}`;
    }
    
    // Keep the date_range_check type but store the generated expression
    updated[index] = {
      ...change,
      expression,
    };
  };

  const handleChangeUpdate = (index: number, field: keyof PropertyChange, value: any) => {
    const updated = [...propertyChanges];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    
    // If updating date range values, regenerate expression
    if (updated[index].change_type === 'date_range_check' && 
        (field === 'date_range_value_if_true' || field === 'date_range_value_if_false')) {
      const change = updated[index] as any;
      const startProp = change.date_range_start_property;
      const endProp = change.date_range_end_property;
      if (startProp) {
        generateDateRangeExpression(updated, index, startProp, endProp || null);
      }
    }
    
    onChange(updated);
  };


  // State for prime artifacts and cross-artifact properties
  const [primeArtifacts, setPrimeArtifacts] = useState<Array<{ id: string; name: string }>>([]);
  const [sourceArtifactProperties, setSourceArtifactProperties] = useState<PropertyDefinition[]>([]);
  const [loadingPrimeArtifacts, setLoadingPrimeArtifacts] = useState(false);
  const [loadingSourceProperties, setLoadingSourceProperties] = useState(false);

  // Fetch prime artifacts on mount
  useEffect(() => {
    const fetchPrimeArtifacts = async () => {
      setLoadingPrimeArtifacts(true);
      try {
        const response = await apiClient.get('/prime-artifacts?skip=0&limit=1000');
        const artifacts = Array.isArray(response.data) ? response.data : (response.data?.items || response.data?.data || []);
        setPrimeArtifacts(artifacts.map((a: any) => ({ id: a.id, name: a.name })));
      } catch (error) {
        console.error('Error fetching prime artifacts:', error);
        setPrimeArtifacts([]);
      } finally {
        setLoadingPrimeArtifacts(false);
      }
    };
    void fetchPrimeArtifacts();
  }, []);

  // Fetch properties for a selected prime artifact
  const fetchSourceArtifactProperties = useCallback(async (artifactId: string) => {
    if (!artifactId) {
      setSourceArtifactProperties([]);
      return;
    }
    setLoadingSourceProperties(true);
    try {
      // Get properties from prime artifact
      const response = await apiClient.get(`/prime-artifacts/${artifactId}/properties`);
      const artifactData = response.data;
      const properties = Array.isArray(artifactData) ? artifactData : (artifactData?.properties || artifactData?.items || []);
      setSourceArtifactProperties(Array.isArray(properties) ? properties : []);
    } catch (error) {
      console.error('Error fetching source artifact properties:', error);
      setSourceArtifactProperties([]);
    } finally {
      setLoadingSourceProperties(false);
    }
  }, []);

  // When source artifact ID changes, fetch its properties
  useEffect(() => {
    if (selectedChange?.source_artifact_id) {
      void fetchSourceArtifactProperties(selectedChange.source_artifact_id);
    } else {
      setSourceArtifactProperties([]);
    }
  }, [selectedChange?.source_artifact_id, fetchSourceArtifactProperties]);

  const getChangeTypeDescription = (type: string): string => {
    const descriptions: Record<string, string> = {
      set: 'Set property to a specific value',
      increment: 'Increase property value by amount',
      decrement: 'Decrease property value by amount',
      copy_from: 'Copy value from another property (same artifact)',
      calculate: 'Calculate value using expression',
      copy_from_artifact: 'Copy value from another artifact item\'s property',
      aggregate_from_artifacts: 'Aggregate values from multiple artifact items (count, sum, avg, etc.)',
    };
    return descriptions[type] || '';
  };

  const renderDetailEditor = () => {
    if (!selectedChange) {
      return (
        <div className={styles.emptyDetail}>
          <p>Select a property change to edit its details.</p>
        </div>
      );
    }

    const selectedProperty = safeProperties.find(
      p => (p.property_key || p.name) === selectedChange.property_key
    );
    const propertyType = selectedProperty?.property_type || selectedProperty?.type || 'string';

    return (
      <Card size="small" className={styles.propertyChangeDetail} key={`change-detail-${selectedIndex}-${selectedChange.change_type}`}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <label>Property:</label>
            <PropertySelector
              properties={safeProperties}
              value={selectedChange.property_key}
              onChange={(propertyKey) => handleChangeUpdate(selectedIndex, 'property_key', propertyKey)}
              disabled={disabled}
              placeholder="Select property to change"
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label>Change Type:</label>
            <Select
              key={`change-type-select-${selectedIndex}`}
              value={selectedChange.change_type || 'set'}
              onChange={(value) => {
                // Update change_type and clear type-specific fields in a single update
                const updated = [...propertyChanges];
                updated[selectedIndex] = {
                  ...updated[selectedIndex],
                  change_type: value,
                  // Clear type-specific fields when changing type
                  ...(value !== 'copy_from' && { source_property: undefined }),
                  ...(value !== 'calculate' && { expression: undefined }),
                  ...(value !== 'date_range_check' && { 
                    date_range_start_property: undefined,
                    date_range_end_property: undefined,
                    date_range_value_if_true: undefined,
                    date_range_value_if_false: undefined
                  }),
                };
                onChange(updated);
              }}
              disabled={disabled}
              style={{ width: '100%' }}
              popupMatchSelectWidth={false}
              styles={{
                popup: {
                  root: {
                    minWidth: '200px',
                    zIndex: 1050
                  }
                }
              }}
            >
              <Option value="set">Set Value</Option>
              <Option value="increment">Increment</Option>
              <Option value="decrement">Decrement</Option>
              <Option value="copy_from">Copy From Property (Same Artifact)</Option>
              <Option value="copy_from_artifact">Copy From Artifact Item</Option>
              <Option value="aggregate_from_artifacts">Aggregate From Artifact Items</Option>
              <Option value="date_range_check">Date Range Check (Set if within range)</Option>
              <Option value="calculate">Calculate (Expression)</Option>
            </Select>
            {selectedChange.change_type && (
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                {getChangeTypeDescription(selectedChange.change_type)}
              </div>
            )}
          </div>

          {selectedChange.change_type === 'set' && (
            <div>
              <label>Target Value:</label>
              {(propertyType === 'number' || propertyType === 'integer' || propertyType === 'float') ? (
                <InputNumber
                  value={selectedChange.target_value}
                  onChange={(value) => handleChangeUpdate(selectedIndex, 'target_value', value)}
                  disabled={disabled}
                  style={{ width: '100%' }}
                  placeholder="Enter value"
                />
              ) : propertyType === 'boolean' ? (
                <Select
                  value={selectedChange.target_value}
                  onChange={(value) => handleChangeUpdate(selectedIndex, 'target_value', value)}
                  disabled={disabled}
                  style={{ width: '100%' }}
                  getPopupContainer={(trigger) => document.body}
                >
                  <Option value={true}>True</Option>
                  <Option value={false}>False</Option>
                </Select>
              ) : (
                <Input
                  value={selectedChange.target_value as any}
                  onChange={(e) => handleChangeUpdate(selectedIndex, 'target_value', e.target.value)}
                  disabled={disabled}
                  placeholder="Enter value"
                />
              )}
            </div>
          )}

          {selectedChange.change_type === 'increment' && (
            <div>
              <label>Increment Amount:</label>
              <InputNumber
                value={selectedChange.target_value as any}
                onChange={(value) => handleChangeUpdate(selectedIndex, 'target_value', value)}
                disabled={disabled}
                style={{ width: '100%' }}
                placeholder="Amount to add"
                min={0}
              />
            </div>
          )}

          {selectedChange.change_type === 'decrement' && (
            <div>
              <label>Decrement Amount:</label>
              <InputNumber
                value={selectedChange.target_value as any}
                onChange={(value) => handleChangeUpdate(selectedIndex, 'target_value', value)}
                disabled={disabled}
                style={{ width: '100%' }}
                placeholder="Amount to subtract"
                min={0}
              />
            </div>
          )}

          {selectedChange.change_type === 'copy_from' && (
            <div>
              <label>Source Property:</label>
              <PropertySelector
                properties={safeProperties.filter(p => {
                  const key = p.property_key || p.name || '';
                  return key !== selectedChange.property_key;
                })}
                value={selectedChange.source_property}
                onChange={(propertyKey) => handleChangeUpdate(selectedIndex, 'source_property', propertyKey)}
                disabled={disabled}
                placeholder="Select property to copy from"
                style={{ width: '100%' }}
              />
            </div>
          )}

          {selectedChange.change_type === 'copy_from_artifact' && (
            <>
              <div>
                <label>Source Prime Artifact:</label>
                <Select
                  value={selectedChange.source_artifact_id}
                  onChange={(value) => {
                    handleChangeUpdate(selectedIndex, 'source_artifact_id', value);
                    // Clear source_property when artifact changes
                    handleChangeUpdate(selectedIndex, 'source_property', undefined);
                  }}
                  disabled={disabled}
                  loading={loadingPrimeArtifacts}
                  style={{ width: '100%' }}
                  placeholder="Search and select prime artifact"
                  showSearch
                  filterOption={(input, option) => {
                    const label = option?.label || option?.children;
                    return String(label).toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {primeArtifacts.map(artifact => (
                    <Option key={artifact.id} value={artifact.id}>
                      {artifact.name}
                    </Option>
                  ))}
                </Select>
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  Select the specific prime artifact to reference
                </div>
              </div>

              {selectedChange.source_artifact_id && (
                <div>
                  <label>Source Property:</label>
                  <PropertySelector
                    properties={sourceArtifactProperties}
                    value={selectedChange.source_property}
                    onChange={(propertyKey) => handleChangeUpdate(selectedIndex, 'source_property', propertyKey)}
                    disabled={disabled || loadingSourceProperties}
                    placeholder={loadingSourceProperties ? "Loading properties..." : "Select property to copy from"}
                    style={{ width: '100%' }}
                  />
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    Property from the source artifact to copy (e.g., "improvement_version")
                  </div>
                </div>
              )}

              <Collapse size="small" style={{ marginTop: 8 }}>
                <Panel header="Optional: Filter to Find Specific Artifact Item" key="filter">
                  <ArtifactItemFilterEditor
                    filter={selectedChange.source_artifact_filter}
                    onChange={(filter: ArtifactItemFilter | undefined) => handleChangeUpdate(selectedIndex, 'source_artifact_filter', filter)}
                    primeArtifacts={primeArtifacts}
                    disabled={disabled}
                  />
                </Panel>
              </Collapse>
            </>
          )}

          {selectedChange.change_type === 'aggregate_from_artifacts' && (
            <>
              <div>
                <label>Source Prime Artifact:</label>
                <Select
                  value={selectedChange.aggregation_artifact_id}
                  onChange={(value) => {
                    handleChangeUpdate(selectedIndex, 'aggregation_artifact_id', value);
                    // Clear aggregation fields when artifact changes
                    handleChangeUpdate(selectedIndex, 'aggregation_property', undefined);
                  }}
                  disabled={disabled}
                  loading={loadingPrimeArtifacts}
                  style={{ width: '100%' }}
                  placeholder="Search and select prime artifact"
                  showSearch
                  filterOption={(input, option) => {
                    const label = option?.label || option?.children;
                    return String(label).toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {primeArtifacts.map(artifact => (
                    <Option key={artifact.id} value={artifact.id}>
                      {artifact.name}
                    </Option>
                  ))}
                </Select>
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  Select the specific prime artifact to aggregate from
                </div>
              </div>

              <div>
                <label>Aggregation Operation:</label>
                <Select
                  value={selectedChange.aggregation_operation}
                  onChange={(value) => {
                    handleChangeUpdate(selectedIndex, 'aggregation_operation', value);
                    // Clear aggregation_property for count operation
                    if (value === 'count') {
                      handleChangeUpdate(selectedIndex, 'aggregation_property', undefined);
                    }
                  }}
                  disabled={disabled}
                  style={{ width: '100%' }}
                  placeholder="Select operation"
                >
                  <Option value="count">Count (number of items)</Option>
                  <Option value="sum">Sum (total of property values)</Option>
                  <Option value="avg">Average (mean of property values)</Option>
                  <Option value="min">Minimum (lowest property value)</Option>
                  <Option value="max">Maximum (highest property value)</Option>
                </Select>
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  {selectedChange.aggregation_operation === 'count' 
                    ? 'Counts the number of artifact items matching the filter'
                    : 'Aggregates values from the specified property'}
                </div>
              </div>

              {selectedChange.aggregation_operation && selectedChange.aggregation_operation !== 'count' && selectedChange.aggregation_artifact_id && (
                <div>
                  <label>Property to Aggregate:</label>
                  <PropertySelector
                    properties={sourceArtifactProperties}
                    value={selectedChange.aggregation_property}
                    onChange={(propertyKey) => handleChangeUpdate(selectedIndex, 'aggregation_property', propertyKey)}
                    disabled={disabled || loadingSourceProperties}
                    placeholder={loadingSourceProperties ? "Loading properties..." : "Select property to aggregate"}
                    style={{ width: '100%' }}
                  />
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    Property to aggregate (e.g., "amount" for sum, "price" for average)
                  </div>
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <label>Filter Criteria:</label>
                <ArtifactItemFilterEditor
                  filter={selectedChange.aggregation_filter}
                  onChange={(filter: ArtifactItemFilter | undefined) => handleChangeUpdate(selectedIndex, 'aggregation_filter', filter)}
                  primeArtifacts={primeArtifacts}
                  disabled={disabled}
                />
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  Filter to select which artifact items to include (e.g., status = "active")
                </div>
              </div>
            </>
          )}

          {selectedChange.change_type === 'date_range_check' && (
            <div>
              <div style={{ marginBottom: 12 }}>
                <label>Start Date Property:</label>
                <PropertySelector
                  properties={safeProperties.filter(p => {
                    const type = p.property_type || p.type || '';
                    return type === 'date' || type === 'datetime';
                  })}
                  value={(selectedChange as any).date_range_start_property}
                  onChange={(value) => {
                    const updated = [...propertyChanges];
                    updated[selectedIndex] = {
                      ...updated[selectedIndex],
                      date_range_start_property: value,
                    } as any;
                    // Auto-generate expression when start property is set
                    const endProp = (updated[selectedIndex] as any).date_range_end_property;
                    if (value) {
                      generateDateRangeExpression(updated, selectedIndex, value, endProp || null);
                    } else {
                      onChange(updated);
                    }
                  }}
                  disabled={disabled}
                  placeholder="Select start date property"
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label>End Date Property (optional):</label>
                <PropertySelector
                  properties={safeProperties.filter(p => {
                    const type = p.property_type || p.type || '';
                    return type === 'date' || type === 'datetime';
                  })}
                  value={(selectedChange as any).date_range_end_property}
                  onChange={(value) => {
                    const updated = [...propertyChanges];
                    updated[selectedIndex] = {
                      ...updated[selectedIndex],
                      date_range_end_property: value,
                    } as any;
                    // Auto-generate expression when both properties are set
                    const startProp = (updated[selectedIndex] as any).date_range_start_property;
                    if (startProp) {
                      generateDateRangeExpression(updated, selectedIndex, startProp, value || null);
                    } else {
                      onChange(updated);
                    }
                  }}
                  disabled={disabled}
                  placeholder="Select end date property (optional)"
                  style={{ width: '100%' }}
                  allowClear
                />
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  If not specified, only checks if current date is after start date
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label>Value if within range:</label>
                {propertyType === 'boolean' ? (
                  <Select
                    value={(selectedChange as any).date_range_value_if_true ?? true}
                    onChange={(value) => {
                      handleChangeUpdate(selectedIndex, 'date_range_value_if_true', value);
                    }}
                    disabled={disabled}
                    style={{ width: '100%' }}
                  >
                    <Option value={true}>True</Option>
                    <Option value={false}>False</Option>
                  </Select>
                ) : (
                  <Input
                    value={(selectedChange as any).date_range_value_if_true ?? 'true'}
                    onChange={(e) => handleChangeUpdate(selectedIndex, 'date_range_value_if_true', e.target.value)}
                    disabled={disabled}
                    placeholder="Value when date is within range"
                  />
                )}
              </div>
              <div style={{ marginBottom: 12 }}>
                <label>Value if outside range:</label>
                {propertyType === 'boolean' ? (
                  <Select
                    value={(selectedChange as any).date_range_value_if_false ?? false}
                    onChange={(value) => {
                      handleChangeUpdate(selectedIndex, 'date_range_value_if_false', value);
                    }}
                    disabled={disabled}
                    style={{ width: '100%' }}
                  >
                    <Option value={true}>True</Option>
                    <Option value={false}>False</Option>
                  </Select>
                ) : (
                  <Input
                    value={(selectedChange as any).date_range_value_if_false ?? 'false'}
                    onChange={(e) => handleChangeUpdate(selectedIndex, 'date_range_value_if_false', e.target.value)}
                    disabled={disabled}
                    placeholder="Value when date is outside range"
                  />
                )}
              </div>
              <div style={{ fontSize: 12, color: '#666', marginTop: 4, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                <strong>Generated Expression:</strong>
                <div style={{ marginTop: 4, fontFamily: 'monospace' }}>
                  {selectedChange.expression || '(Select date properties to generate expression)'}
                </div>
              </div>
            </div>
          )}

          {selectedChange.change_type === 'calculate' && (
            <div>
              <label>Expression:</label>
              <Input
                value={selectedChange.expression}
                onChange={(e) => handleChangeUpdate(selectedIndex, 'expression', e.target.value)}
                disabled={disabled}
                placeholder="e.g., {{property1}} + {{property2}} or {{now}}"
              />
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                Use {'{{property_key}}'} to reference other properties, {'{{now}}'} for current time
              </div>
            </div>
          )}
        </Space>
      </Card>
    );
  };

  return (
    <div className={`${styles.propertyChangesEditor} ${styles.listDetailLayout}`}>
      <div className={styles.editorHeader}>
        <h3>
          Property Changes
          <Tooltip title="Define how properties change when this transition executes">
            <InfoCircleOutlined style={{ marginLeft: 8, fontSize: 14, color: '#faad14' }} />
          </Tooltip>
        </h3>
        <p style={{ color: '#666', fontSize: 14, marginTop: 8 }}>
          View all property changes on the left and edit details on the right.
        </p>
      </div>

      <div className={styles.propertyChangesBody}>
        <div className={styles.changesListPane}>
          <div className={styles.listHeader}>
            <span>Properties</span>
            {!disabled && (
              <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={handleAddChange}>
                Add
              </Button>
            )}
          </div>
          {propertyChanges.length > 0 ? (
            <div className={styles.changesList}>
              {propertyChanges.map((change, index) => {
                const displayName =
                  safeProperties.find(p => (p.property_key || p.name) === change.property_key)?.name ||
                  change.property_key ||
                  'Select property';
                return (
                  <div
                    key={index}
                    className={`${styles.changeRow} ${index === selectedIndex ? styles.selected : ''}`}
                    onClick={() => setSelectedIndex(index)}
                  >
                    <div className={styles.rowMain}>
                      <span className={styles.rowTitle}>{displayName}</span>
                      <Tag color="blue" style={{ marginLeft: 8 }}>
                        {change.change_type}
                      </Tag>
                    </div>
                    {!disabled && (
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveChange(index);
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <p>No property changes defined. Click "Add" to get started.</p>
            </div>
          )}
        </div>

        <div className={styles.changesDetailPane}>
          {renderDetailEditor()}
        </div>
      </div>
    </div>
  );
};
